"use client";

import { DealLabel, FavoriteButton, PriceTrackingButton, WishlistChart } from "@/components";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useDealType } from "@/hooks/useDealType";
import { useImpressionTracking } from "@/hooks/useImpressionTracking";
import { useMarketDifference } from "@/hooks/useMarketDifference";
import { useShareListing } from "@/hooks/useShareListing";
import { type Listing } from "@/services/api";
import { capitalizeWords, formatBrandName } from "@/utils/helpers/stringHelpers";
import Image from "next/image";

interface WishlistItemHorizontalProps {
    listing: Listing;
    showDealLabel?: boolean;
}

const WishlistItemHorizontal = ({ listing, showDealLabel = true }: WishlistItemHorizontalProps) => {
    const { formatPrice } = useCurrency();
    const dealType = useDealType(listing);
    const marketDifference = useMarketDifference(listing);
    const handleShareClick = useShareListing(listing);

    const impressionRef = useImpressionTracking(listing, {
        threshold: 0.5,
        minVisibleTime: 1000,
        metadata: {
            source: "wishlist_horizontal",
            show_deal_label: showDealLabel,
        }
    });

    return (
        <div className="bg-white pb-8 mb-8" ref={impressionRef}>
            <div className="flex gap-6 h-[340px] bg-white">
                <div className="w-80 flex-shrink-0 relative bg-white">
                    <a href={`/listings/${listing.ss_slug}`} className="block">
                        <div className="aspect-square overflow-hidden relative bg-white">
                            <Image
                                src={listing.s3_image_urls[0] || "/placeholder-image.jpg"}
                                alt={`${listing.product.brand} ${listing.product.model}`}
                                fill
                                className="object-contain hover:scale-105 transition-transform p-6"
                                sizes="320px"
                            />
                        </div>
                    </a>

                    <div className="absolute top-3 left-3 right-3 flex justify-between items-start">
                        <div>
                            {showDealLabel && dealType !== 'none' && (
                                <DealLabel type={dealType} size="small" />
                            )}
                        </div>

                        <div className="flex gap-2">
                            <FavoriteButton listing={listing} />
                            <PriceTrackingButton listing={listing} />
                        </div>
                    </div>
                </div>

                <div className="flex-1 min-w-0 flex flex-col">
                    <div className="flex gap-6 mb-6 xl:gap-6 lg:gap-4">
                        <div className="flex-1 min-w-0">
                            <a href={`/listings/${listing.ss_slug}`} className="block">
                                <h3 className="font-bodoni text-lg font-medium text-black leading-tight truncate xl:text-lg lg:text-base">
                                    Pre-owned {formatBrandName(listing.product.brand)}
                                </h3>
                                <h4 className="font-raleway text-base text-black leading-tight truncate xl:text-base lg:text-sm">
                                    {capitalizeWords(listing.product.model)}
                                </h4>

                                <div className="text-lg font-raleway font-semibold text-black mt-3 mb-2 xl:text-lg lg:text-base">
                                    {formatPrice(listing.current_price)}
                                </div>

                                {marketDifference && (
                                    <div className="font-raleway text-sm text-gray-600 mb-4 truncate xl:text-sm lg:text-xs">
                                        Currently{' '}
                                        <span className={marketDifference.isAbove ? 'text-red-600' : 'text-green-700'}>
                                            {marketDifference.amount} {marketDifference.direction}
                                        </span>
                                        {' '}Sold Market Average
                                    </div>
                                )}

                                {listing.vendor && (
                                    <div className="font-raleway capitalize text-sm text-gray-500 truncate xl:text-sm lg:text-xs">
                                        {listing.vendor}
                                    </div>
                                )}
                            </a>
                        </div>

                        <div className="shrink min-w-0">
                            <button
                                onClick={handleShareClick}
                                className="
                                btn-raleway shrink min-w-0 max-w-full
                                flex items-center justify-center
                                px-2 py-2 text-[11px]
                                sm:px-3 sm:text-xs
                                md:px-3 md:text-xs
                                lg:px-2 lg:text-xs
                                xl:px-3 xl:text-xs
                                2xl:px-4 2xl:text-sm
                                border border-black bg-white hover:bg-gray-50 transition-colors
                                whitespace-normal break-words truncate
                                "
                            >
                                <span className="block truncate">THROW IT IN THE GROUP CHAT</span>
                            </button>
                        </div>
                    </div>

                    <div className="flex-1 flex items-end relative">
                        <div className="w-full">
                            <WishlistChart listing={listing} variant="compact" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WishlistItemHorizontal;
